// checkout/utils/loqate/helpers.ts

import type { LoqateLookupItem, LoqateLookupResponsePossibleShapes } from './types';

/**
 * Build context path from window.serverData.contextPath
 */
export function getContextPath(): string {
	const win = window as unknown as {
		serverData?: { contextPath?: string };
	};
	return win.serverData?.contextPath ?? '';
}

/**
 * Normalise whatever the backend returns into a clean array of LoqateLookupItem.
 *
 * Handles:
 *  - [ { value, type, label, description } ]  (current backend)
 *  - { Items: [...] } / { items: [...] }      (future-proof)
 *  - [ { Id, Type, Text, Description } ]      (Loqate-ish shape)
 *
 * @param {unknown} data Raw JSON from the backend
 * @returns {LoqateLookupItem[]} Normalised items
 */
export function normaliseLookupItems(data: unknown): LoqateLookupItem[] {
	let rawItems: unknown;

	if (Array.isArray(data)) {
		rawItems = data;
	} else if (
		data !== null &&
		typeof data === 'object' &&
		('Items' in (data as LoqateLookupResponsePossibleShapes) ||
			'items' in (data as LoqateLookupResponsePossibleShapes))
	) {
		const obj = data as LoqateLookupResponsePossibleShapes;
		rawItems = obj.Items ?? obj.items;
	} else {
		return [];
	}

	if (!Array.isArray(rawItems)) return [];

	const result: LoqateLookupItem[] = [];

	for (const raw of rawItems) {
		if (raw === null || typeof raw !== 'object') continue;

		const r = raw as Record<string, unknown>;

		const value =
			(r.value as string | undefined) ??
			(r.Value as string | undefined) ??
			(r.id as string | undefined) ??
			(r.Id as string | undefined);

		const type =
			(r.type as string | undefined) ??
			(r.Type as string | undefined) ??
			(r.TYPE as string | undefined);

		const label =
			(r.label as string | undefined) ??
			(r.Label as string | undefined) ??
			(r.text as string | undefined) ??
			(r.Text as string | undefined);

		const description =
			(r.description as string | undefined) ??
			(r.Description as string | undefined) ??
			(r.desc as string | undefined);

		if (!value || !type || !label) continue;

		result.push({
			Id: value,
			Type: type,
			// Keep backend <strong> tags in the label so highlighting is preserved.
			Text: label,
			Description: description,
		});
	}

	return result;
}

/**
 * Get the first non-empty value from possible keys.
 *
 * @param {Record<string, unknown>} obj Source object
 * @param {string[]} keys Ordered candidate keys
 * @returns {string} Found string value or empty string
 */
export function getFirstString(obj: Record<string, unknown>, keys: string[]): string {
	for (const key of keys) {
		const value = obj[key];
		if (typeof value === 'string') {
			const trimmed = value.trim();
			if (trimmed) return trimmed;
		}
	}
	return '';
}
